import { LogEvents } from "../../../utils/LogEvents";
import { RetryServiceCaller, RetryServiceCallUntilSuccessOrTimeout } from "../../utils/Utils";
import { ErrorStringFromException, isEmptyString } from "../../../utils/utils";
import { LogEvent, LogSingleEvent } from "../../TelemetryLogger/LogEvent";
import { UIAuthProvider, GetAccessTokenOptions, IdentityType } from "../../interfaces/IAuthenticationProvider";
import { ILogData, LogEventNames } from "../../interfaces/ITelemetryLogger";
import { HostAppSettings, HostAppSettingName, GetSSOLoginUrl } from "../HostAppSettings";
import { AUTH_CONTEXT_LOAD_DELAY_MILLIS, AUTH_CONTEXT_LOAD_DELAYMSAL_MILLIS, NAA_MSA_CLIENT_ID, NAA_AAD_CLIENT_ID, HOST_PLATFORM_WIN32 } from "../../../AppConstants";
import { HostAppUtil } from "../HostAppUtil";
import { GetPrimaryIdentityInfo } from "./IdentityUtil";
import { isNestedAppAuthEnabled, enableMsal5 } from "../../../utils/ChangeGate";


async function GetNaaClientId(): Promise<string> {
    try {
        const identityInfo = await GetPrimaryIdentityInfo();
        return identityInfo.type === IdentityType.ODP ? NAA_MSA_CLIENT_ID : NAA_AAD_CLIENT_ID;
    } catch {
        return NAA_MSA_CLIENT_ID;
    }
}

function GetNaaRedirectUri(): string {
    const isWin32 = HostAppUtil.GetHostAppPlatform() === HOST_PLATFORM_WIN32;
    return isWin32 ? `brk-multihub://${window.location.hostname}` : GetSSOLoginUrl();
}

enum GetAccessTokenDiagKeys {
    Succeeded = 'Succeeded',
    FailureReason = 'FailureReason',
    UpnMismatchType1 = 'UpnMismatchType1',
    UpnMismatchType2 = 'UpnMismatchType2',
    UpnMismatchDiagFailReason = 'UpnMismatchDiagFailReason'
}

// This class contains shared implementations for PpeAuthenticationProvider and ProdAuthenticationProvider
export class AuthSharedComponent {
    private officeFirstPartyAuthLoaded: boolean = false;
    private authenticationTask: Promise<void> | null = null;
    private uiAuthProvider: UIAuthProvider | null = null;
    private naaInitTask: Promise<boolean> | null = null;
    private msalCacheCleared: boolean = false;

    Authenticate(): void {
        LogEvents.ServiceAuthentication.Start();

        (<any>OfficeFirstPartyAuth).delay = AUTH_CONTEXT_LOAD_DELAY_MILLIS;
        (<any>OfficeFirstPartyAuth).delayMsal = AUTH_CONTEXT_LOAD_DELAYMSAL_MILLIS;

        (<any>OfficeFirstPartyAuth).authFlow = "authcode";

        const ShouldEnableFlightAuthVersion: boolean = HostAppSettings.IsSettingTrue(HostAppSettingName.EnableFlightAuthVersion);

        (<any>OfficeFirstPartyAuth).upnCheck = false;


        if (ShouldEnableFlightAuthVersion) {
            (<any>OfficeFirstPartyAuth).authVersion = 'flight';
        }

        if (this.officeFirstPartyAuthLoaded) {
            LogEvents.ServiceAuthentication.AddResponse({ isSuccessful: true, message: "Success", isFinalResponse: true });
            return;
        }

        this.authenticationTask = RetryServiceCallUntilSuccessOrTimeout(
            LogEvents.ServiceAuthentication /*logServiceEvent*/,
            () => OfficeFirstPartyAuth.load(GetSSOLoginUrl()) /*throwingServiceCall*/,
            false /*shouldRetryOnNull*/,
            RetryServiceCaller.OfficeFirstPartyAuthLoad,
            () => true,
            () => false
        ).promise.then(() => {
            this.officeFirstPartyAuthLoaded = true;
        });

        if (isNestedAppAuthEnabled()) {
            this.naaInitTask = GetNaaClientId()
                .then(clientId => (OfficeFirstPartyAuth as any).NestedAppAuth.load(clientId, GetNaaRedirectUri()))
                .then(() => true)
                .catch(() => false);
        }
    }

    async GetAccessToken(options: GetAccessTokenOptions): Promise<string> {
        LogEvents.ServiceGetAccessToken.Start();
        if (!this.officeFirstPartyAuthLoaded) {
            if (this.authenticationTask == null) {
                const message: string = "GetAccessToken: Call Authenticate before calling GetAccessToken!";
                LogEvents.ServiceGetAccessToken.AddResponse({ isSuccessful: false, message, isFinalResponse: true});
                throw new Error(message);
            } else {
                try {
                    await this.authenticationTask;
                } catch (error) {
                    LogEvents.ServiceGetAccessToken.AddResponse({ isSuccessful: false, message: "GetAccessToken: Authenticate failed with error: " + ErrorStringFromException(error), isFinalResponse: true});
                    throw error;
                }
            }
        }

        if (this.naaInitTask) {
            const naaReady = await this.naaInitTask;
            if (naaReady) {
                const identityInfo = await GetPrimaryIdentityInfo();
                if (identityInfo.type === IdentityType.ODP || identityInfo.type === IdentityType.ODB) {
                    const token = await this.CallNaaGetAccessToken(options, identityInfo.type);
                    LogEvents.ServiceGetAccessToken.AddResponse({ isSuccessful: true, message: "NAA token acquired", isFinalResponse: true });
                    return token;
                }
            }
        }

        let popupRequired: boolean = false;
        let popupWithAppUIRequired: boolean = false;
        let token: OfficeFirstPartyAuth.AuthResult = await RetryServiceCallUntilSuccessOrTimeout(
            LogEvents.ServiceGetAccessToken /*logServiceEvent*/,
            async () => {
                try {
                    let tokenPromise:Promise<OfficeFirstPartyAuth.AuthResult> | null = null;
                    if (popupWithAppUIRequired) {
                        if (this.uiAuthProvider == null) {
                            throw new Error('No UI auth provider was registered. Unable to show an AuthPopup');
                        }

                        tokenPromise = this.uiAuthProvider(async() => await this.CallOFPAGetAccessToken(options, true /*popupRequired*/));
                    } else {
                        tokenPromise = this.CallOFPAGetAccessToken(options, popupRequired /*popupRequired*/)
                    }

                    popupRequired = true;
                    return await tokenPromise;
                } catch (e) {
                    return Promise.reject(e);
                }
            } /*throwingServiceCall*/,
            true /*shouldRetryOnNull*/,
            RetryServiceCaller.CallOFPAGetAccessToken,
            (e) => {
                if (e && e.code == 'upn_mismatch') {
                    this.LogDiagnosticTelemForUpnMismatches();
                }

                if (e && e.code == 'interaction_required' && e.message && e.message.includes('AADSTS160021') && !this.msalCacheCleared) {
                    this.clearMsalCacheEntries();
                    this.msalCacheCleared = true;
                    popupRequired = false;
                    return true;
                }

                if (e && (e.code == 'monitor_window_timeout' || e.code == 'timed_out') && !this.msalCacheCleared) {
                    this.clearMsalCacheEntries();
                    this.msalCacheCleared = true;
                    popupRequired = false;
                    return true;
                }

                // MSAL5 does not support popup auth - no point retrying
                if (e && e.code == 'popup_auth_flow_not_supported') {
                    return false;
                }

                // In MSAL5, login_required means silent auth failed and popup is not available.
                // Fail fast instead of attempting popup which will just throw popup_auth_flow_not_supported.
                if (enableMsal5 && e && e.code == 'login_required') {
                    return false;
                }

                if (popupWithAppUIRequired) {
                    return false;
                } else if (e && e.code == 'popup_window_error') {
                    popupWithAppUIRequired = true;
                }

                return true;
            } /*shouldRetryError*/,
            () => popupRequired /*shouldIgnoreTimeout*/
        ).promise;

        return token.accessToken;
    }

    SetUIAuthProvider(uiAuthProvider: UIAuthProvider): void {
        this.uiAuthProvider = uiAuthProvider;
    }

    private CallOFPAGetAccessToken(options: GetAccessTokenOptions, popupRequired: boolean = false): Promise<any> {
        const GATlogEvent = new LogEvent<GetAccessTokenDiagKeys>(LogEventNames.GetAccessTokenDiag);
        try {
            GATlogEvent.Start();
            
            const requestOptions: any = { resource: options.resource };
            if (options.scope) {
                requestOptions.scope = options.scope;
            }
            if (options.policy) {
                requestOptions.policy = options.policy;
            }
            
            const tokenPromise:Promise<OfficeFirstPartyAuth.AuthResult> = OfficeFirstPartyAuth.getAccessToken(requestOptions, popupRequired ? { popup: true } : undefined) /*throwingServiceCall*/
            GATlogEvent.End([{key: GetAccessTokenDiagKeys.Succeeded, value: true}]);
            return tokenPromise;
        } catch(e) {
            GATlogEvent.End([{key: GetAccessTokenDiagKeys.Succeeded, value: false}, {key: GetAccessTokenDiagKeys.FailureReason, value: ErrorStringFromException(e)} ]);
            throw e;
        }
    }

    private LogDiagnosticTelemForUpnMismatches(): void {
        OfficeFirstPartyAuth.getPrimaryIdentityInfo().then(
            (primaryIdentityInfo: OfficeCore.OfficeIdentityInfo) => {
                let data: ILogData<GetAccessTokenDiagKeys>[] = [];
                if (this.isStringAPhoneNumber(primaryIdentityInfo.email.split('@')[0])) {
                    data.push({ key: GetAccessTokenDiagKeys.UpnMismatchType1, value: true });
                } else if (this.isStringAPhoneNumber(primaryIdentityInfo.uniqueId)) {
                    data.push({ key: GetAccessTokenDiagKeys.UpnMismatchType2, value: true });
                }

                if (data.length != 0) {
                    LogSingleEvent<GetAccessTokenDiagKeys>(LogEventNames.GetAccessTokenDiag, data);
                }
            } /*onFulfilled*/,
            (reason: any) => {
                LogSingleEvent<GetAccessTokenDiagKeys>(LogEventNames.GetAccessTokenDiag,
                    [{ key: GetAccessTokenDiagKeys.UpnMismatchDiagFailReason, value: ErrorStringFromException(reason) }]);
            } /*onRejected*/
        );
    }

    private MapToNaaScopes(options: GetAccessTokenOptions, identityType: IdentityType): string[] {
        const resource = (options.resource || '').toLowerCase();
        if (resource.includes('augloop')) {
            // Consumer (MSA/ODP) uses AugLoop.Copilot; enterprise (AAD/ODB) uses AugLoop.All.
            return [identityType === IdentityType.ODP ? `${resource}/AugLoop.Copilot` : `${resource}/AugLoop.All`];
        }
        if (resource.includes('graph.microsoft.com')
            || resource.includes('graph.microsoft-ppe.com')
            || resource.includes('graph.microsoft.us')
            || resource.includes('dod-graph.microsoft.us')
            || resource.includes('graph.eaglex.ic.gov')
            || resource.includes('graph.microsoft.scloud')) {
            return [`${resource}/Files.ReadWrite.All`];
        }
        if (resource === 'onedrive.readwrite') {
            return ['onedrive.readwrite'];
        }
        return [`${resource}/.default`];
    }

    private async CallNaaGetAccessToken(options: GetAccessTokenOptions, identityType: IdentityType): Promise<string> {
        const clientId = identityType === IdentityType.ODP ? NAA_MSA_CLIENT_ID : NAA_AAD_CLIENT_ID;
        const scopes = this.MapToNaaScopes(options, identityType);
        const result = await (OfficeFirstPartyAuth as any).NestedAppAuth.getAccessToken({
            clientId,
            scopes
        });
        return result.accessToken;
    }

    private clearMsalCacheEntries(): void {
        try {
            // localStorage keys should be enumerated via Storage.length/key(i), not Object.keys(localStorage)
            const keysToRemove: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key) {
                    keysToRemove.push(key);
                }
            }

            keysToRemove
                .filter(k => k.startsWith('msal.') || k.includes('-login.microsoftonline'))
                .forEach(k => localStorage.removeItem(k));
        } catch {
            // Ignore errors from localStorage access
        }
    }

    private isStringAPhoneNumber(input: string): boolean {
        if (isEmptyString(input)) {
          return false;
        }
        // Regex from https://stackoverflow.com/questions/4338267/validate-phone-number-with-javascript
        return (input.match(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im) != null);
    }
}
